interface BaseCategory {
  category: string;
}

const CATEGORIES_MAX_LENGTH = 42;

export const getCategoriesLength = (categories: BaseCategory[]): number => {
  return categories.map((c) => c.category.length).reduce((a, b) => a + b, 0);
};

export const truncateCategories = <T extends BaseCategory>(
  categories: T[],
): T[] => {
  while (getCategoriesLength(categories) > CATEGORIES_MAX_LENGTH) {
    categories.shift();
  }

  return categories;
};

interface CategoryResourceType {
  title: string;
  path?: string;
  permalink?: string;
  parent?: CategoryResourceType | null;
}

interface DisplayCategory extends BaseCategory {
  path: string;
}

const formatDisplayCategory = (
  category: CategoryResourceType,
): DisplayCategory => {
  return {
    category: category.title,
    path: category.path || "",
  };
};

interface BreadcrumbCategory extends BaseCategory {
  url: string;
}

const formatBreadcrumbCategory = (
  category: CategoryResourceType,
): BreadcrumbCategory => {
  return {
    category: category.title,
    url: category.permalink || "",
  };
};

// Assign levels to each category
const organizeCategoriesByLevel = (
  categories: CategoryResourceType[],
): CategoryResourceType[][] => {
  const levels: CategoryResourceType[][] = [];
  categories.forEach((cat) => {
    let level = 0;
    for (let current = cat.parent; current; current = current.parent) {
      level++;
    }
    (levels[level] ?? (levels[level] = [])).push(cat);
  });
  return levels;
};

const canCategoryBeAdded = (
  hierarchy: BaseCategory[],
  cat: CategoryResourceType,
  maxNumberOfCategories: number,
): boolean => {
  return (
    hierarchy.length < maxNumberOfCategories &&
    !hierarchy.some((h) => h.category === cat.title)
  );
};

/**
 * Creates a factory function for generating a category hierarchy or breadcrumb trail.
 *
 * This utility supports two strategies for constructing the hierarchy:
 * - "hierarchy": Recursively traverses up the parent chain of the primary category,
 *   collecting ancestors up to a specified number of generations.
 * - "flat": Gathers all ancestors of all provided categories, then assembles a flat
 *   list of categories from the top level down, up to the desired number of generations.
 *
 * The returned function formats each category using the provided `format` callback,
 * and returns an array of formatted categories representing the hierarchy or breadcrumb.
 *
 * @typeParam T - The type returned by the `format` function, extending `BaseCategory`.
 * @param format - A function that transforms a `CategoryResourceType` into type `T`.
 * @returns A function that, given a primary category, number of generations, an optional
 *          list of categories, and a breadcrumb strategy, returns an array of formatted
 *          categories representing the hierarchy.
 */
const categoryHierarchyFactory = <T extends BaseCategory>(
  format: (c: CategoryResourceType) => T,
) => {
  return (
    primaryCategory: CategoryResourceType,
    maxNumberOfCategories: number = 3,
    categories: CategoryResourceType[] = [],
    breadcrumbStrategy: "hierarchy" | "flat" = "hierarchy",
  ): T[] => {
    const hierarchy: T[] = [];

    // Hierarchy strategy: recursively traverse up the parent chain of the primary category
    function traversePrimaryHierarchy(
      category: CategoryResourceType,
      depth: number,
    ) {
      if (depth > 0) {
        hierarchy.unshift(format(category));
        if (category.parent) {
          traversePrimaryHierarchy(category.parent, depth - 1);
        }
      }
    }

    // Flat strategy (mostly for The Verge): collect all ancestors of all categories,
    // then from the top level down, pull in categories until we reach the desired number of generations.
    function traverseFlatHierarchy(
      primary: CategoryResourceType,
      categories: CategoryResourceType[] = [],
    ) {
      // Put the primary category in the first slot
      hierarchy.push(format(primary));

      // Assign levels to each category
      const levels = organizeCategoriesByLevel(categories);

      // Add categories by level (top-level first)
      for (const cats of levels) {
        if (!Array.isArray(cats)) {
          continue;
        }
        for (const cat of cats) {
          if (canCategoryBeAdded(hierarchy, cat, maxNumberOfCategories)) {
            hierarchy.push(format(cat));
          }
        }
      }
    }

    if (breadcrumbStrategy === "flat") {
      traverseFlatHierarchy(primaryCategory, categories);
    } else {
      traversePrimaryHierarchy(primaryCategory, maxNumberOfCategories);
    }

    return truncateCategories(hierarchy);
  };
};

export const getDisplayCategories = categoryHierarchyFactory<DisplayCategory>(
  formatDisplayCategory,
);

export const getBreadcrumbCategories =
  categoryHierarchyFactory<BreadcrumbCategory>(formatBreadcrumbCategory);

export type CategoryListItem = {
  readonly lastContentPublishedAt: string | null;
  readonly permalink: string;
  readonly title: string;
  readonly displayAsChildCategory?: boolean;
  readonly displayAsChildSuperCat?: boolean;
};

export const getDisplaySubcategories = (
  categories: readonly CategoryListItem[],
  limit: number = 8,
) => {
  const displayableCategories = categories.filter(
    (c) =>
      c.displayAsChildCategory !== false && c.displayAsChildSuperCat !== false,
  );
  let filteredCategories = [...displayableCategories];

  if (displayableCategories.length > 7) {
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    filteredCategories = displayableCategories.filter((c) => {
      return new Date(c.lastContentPublishedAt || "") > sixMonthsAgo;
    });
  }

  return filteredCategories
    .sort((a, b) => {
      return (
        new Date(b.lastContentPublishedAt || "").valueOf() -
        new Date(a.lastContentPublishedAt || "").valueOf()
      );
    })
    .map((c) => ({
      text: c.title,
      url: c.permalink,
    }))
    .slice(0, limit);
};
